import { useCallback, useEffect, useState } from "react";
import { ethers } from "ethers";
import {
  provider, ERC20_ABI, DISPENSER_ABI, fmtUnits, shortHex, ago, type Manifest,
} from "../chain";
import { Mark } from "../Brand";

const KEY = "settlx.devnet.wallet.v1";

interface Move { hash: string; dir: "in" | "out"; other: string; amount: bigint; block: number; ts: number }

/**
 * A phone-shaped wallet for exercising the devnet by hand.
 *
 * This is a DEVELOPER tool and it holds a raw private key in localStorage,
 * which is exactly what the real product exists to avoid — SettlX users get a
 * passkey and a threshold key they never see. Nothing here is a model for
 * that; it is a way to hold funds and press buttons while testing. The screen
 * says so rather than leaving someone to infer it.
 */
export default function Wallet({ m }: { m: Manifest }) {
  const [pk, setPk] = useState<string | null>(() => localStorage.getItem(KEY));
  const [native, setNative] = useState<bigint>();
  const [tokens, setTokens] = useState<bigint>();
  const [moves, setMoves] = useState<Move[]>([]);
  const [view, setView] = useState<"home" | "send" | "receive">("home");
  const [to, setTo] = useState(""); const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string }>();

  const address = pk ? new ethers.Wallet(pk).address : undefined;

  const refresh = useCallback(async () => {
    if (!address) return;
    try {
      const p = provider();
      const token = new ethers.Contract(m.contracts.token, ERC20_ABI, p);
      setNative(await p.getBalance(address));
      setTokens((await token.balanceOf(address)) as bigint);

      // Transfers touching this address. A devnet is small enough to scan
      // from genesis; on anything bigger this belongs in an indexer.
      const inbound = await token.queryFilter(token.filters.Transfer(null, address));
      const outbound = await token.queryFilter(token.filters.Transfer(address, null));
      const all = [...inbound, ...outbound] as ethers.EventLog[];
      const withTs = await Promise.all(all.map(async (e) => {
        const b = await e.getBlock();
        return {
          hash: e.transactionHash,
          dir: (e.args[1] as string).toLowerCase() === address.toLowerCase() ? "in" : "out",
          other: (e.args[1] as string).toLowerCase() === address.toLowerCase()
            ? (e.args[0] as string) : (e.args[1] as string),
          amount: e.args[2] as bigint,
          block: e.blockNumber,
          ts: b.timestamp,
        } as Move;
      }));
      withTs.sort((a, b) => b.block - a.block);
      setMoves(withTs.slice(0, 12));
    } catch { /* overview reports an unreachable chain */ }
  }, [address, m]);

  useEffect(() => { refresh(); const h = setInterval(refresh, 6000); return () => clearInterval(h); }, [refresh]);

  const create = () => {
    const w = ethers.Wallet.createRandom();
    localStorage.setItem(KEY, w.privateKey);
    setPk(w.privateKey);
  };
  const forget = () => { localStorage.removeItem(KEY); setPk(null); setMoves([]); setNative(undefined); setTokens(undefined); };

  const topUp = async () => {
    if (!address) return;
    setBusy(true); setMsg(undefined);
    try {
      const relay = ethers.Wallet.createRandom().connect(provider());
      const d = new ethers.Contract(m.contracts.dispenser, DISPENSER_ABI, relay);
      await (await d.drip(address, { gasPrice: 0, gasLimit: 250_000 })).wait();
      setMsg({ kind: "ok", text: "topped up" });
      refresh();
    } catch (e) {
      const raw = (e as Error).message;
      setMsg({ kind: "err", text: /TooSoon/.test(raw) ? "cooldown has not elapsed yet" : raw });
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!pk) return;
    setBusy(true); setMsg(undefined);
    try {
      const w = new ethers.Wallet(pk, provider());
      const token = new ethers.Contract(m.contracts.token, ERC20_ABI, w);
      const value = ethers.parseUnits(amt, m.token.decimals);
      const tx = await token.transfer(to, value, { gasPrice: 0, gasLimit: 120_000 });
      await tx.wait();
      setMsg({ kind: "ok", text: `sent — ${shortHex(tx.hash, 10, 8)}` });
      setAmt(""); setTo(""); setView("home"); refresh();
    } catch (e) { setMsg({ kind: "err", text: (e as Error).message }); }
    finally { setBusy(false); }
  };

  if (!pk) {
    return (
      <>
        <h1>Wallet</h1>
        <p className="sub">A throwaway account for poking the devnet by hand.</p>
        <div className="phone">
          <div className="phone-bar">
            <Mark size={15} plate="var(--panel-2)" />
            <span className="wordmark">Secure<span className="x">X</span></span>
          </div>
          <div className="phone-body">
            <p className="dim" style={{ fontSize: 14 }}>
              No account on this device yet. Creating one generates a key in your browser
              and stores it in <code>localStorage</code>.
            </p>
            <button className="go" style={{ width: "100%" }} onClick={create}>Create an account</button>
          </div>
        </div>
        <div className="note warn" style={{ marginTop: 18 }}>
          This is a developer tool and it keeps a raw private key in the browser — precisely
          what the real product exists to avoid. SettlX users get a passkey and a 2-of-3
          threshold key no single party ever holds. Nothing on this screen is a model for that.
        </div>
      </>
    );
  }

  return (
    <>
      <h1>Wallet</h1>
      <p className="sub">A throwaway account for poking the devnet by hand.</p>

      <div className="phone">
        <div className="phone-bar">
            <Mark size={15} plate="var(--panel-2)" />
            <span className="wordmark">Secure<span className="x">X</span></span>
          </div>
        <div className="phone-body">
          {view === "home" && (
            <>
              <div className="balance">
                <div className="big">{tokens === undefined ? "—" : fmtUnits(tokens, m.token.decimals)}</div>
                <div className="cur">{m.token.symbol}</div>
                <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
                  {native === undefined ? "" : `${ethers.formatEther(native)} SX for gas that costs nothing`}
                </div>
              </div>

              <div className="acts">
                <button className="ghost" onClick={() => setView("send")} disabled={!tokens}>Send</button>
                <button className="ghost" onClick={() => setView("receive")}>Receive</button>
              </div>
              <button className="go" style={{ width: "100%", marginTop: 4 }} onClick={topUp} disabled={busy}>
                {busy ? <><span className="spin" /> asking the faucet</> : "Top up from faucet"}
              </button>
              {msg && <p className={msg.kind === "ok" ? "ok" : "err"} style={{ fontSize: 13 }}>{msg.text}</p>}

              <h2 style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--dim)" }}>
                Activity
              </h2>
              {moves.length === 0 && <p className="dim" style={{ fontSize: 13 }}>Nothing yet.</p>}
              {moves.map((mv) => (
                <div key={mv.hash + mv.dir} className="row" style={{ justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid var(--border)" }}>
                  <div>
                    <div style={{ fontSize: 14 }}>{mv.dir === "in" ? "Received" : "Sent"}</div>
                    <div className="dim mono" style={{ fontSize: 11 }}>{shortHex(mv.other, 6, 4)} · {ago(mv.ts)}</div>
                  </div>
                  <div style={{ color: mv.dir === "in" ? "var(--good)" : "var(--text)", fontWeight: 600 }}>
                    {mv.dir === "in" ? "+" : "−"}{fmtUnits(mv.amount, m.token.decimals)}
                  </div>
                </div>
              ))}
            </>
          )}

          {view === "send" && (
            <>
              <h2 style={{ marginTop: 0 }}>Send {m.token.symbol}</h2>
              <div className="grid" style={{ gap: 10 }}>
                <input className="mono" placeholder="0x… recipient" value={to}
                  onChange={(e) => setTo(e.target.value.trim())} spellCheck={false} />
                <input placeholder={`amount in ${m.token.symbol}`} value={amt}
                  onChange={(e) => setAmt(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" />
                <button className="go" onClick={send}
                  disabled={busy || !ethers.isAddress(to) || !amt || Number(amt) <= 0}>
                  {busy ? <><span className="spin" /> sending</> : "Send"}
                </button>
                <button className="ghost" onClick={() => { setView("home"); setMsg(undefined); }}>Back</button>
              </div>
              {msg && <p className={msg.kind === "ok" ? "ok" : "err"} style={{ fontSize: 13 }}>{msg.text}</p>}
            </>
          )}

          {view === "receive" && (
            <>
              <h2 style={{ marginTop: 0 }}>Receive</h2>
              <p className="dim" style={{ fontSize: 13 }}>Your address on this devnet:</p>
              <div className="log" style={{ maxHeight: "none" }}>{address}</div>
              <button className="ghost" style={{ width: "100%", marginTop: 10 }}
                onClick={() => navigator.clipboard?.writeText(address!)}>Copy address</button>
              <button className="ghost" style={{ width: "100%", marginTop: 8 }}
                onClick={() => setView("home")}>Back</button>
            </>
          )}
        </div>
      </div>

      <div className="row" style={{ justifyContent: "center", marginTop: 14 }}>
        <span className="dim mono" style={{ fontSize: 12 }}>{shortHex(address!, 10, 8)}</span>
        <button className="ghost" onClick={forget}>Forget this account</button>
      </div>

      <div className="note warn" style={{ marginTop: 18 }}>
        A raw private key in <code>localStorage</code> — a developer convenience and the
        exact thing the product avoids. Real users hold a passkey; their signing key is a
        2-of-3 threshold key that is never assembled by anyone, including the platform.
      </div>
    </>
  );
}
